import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import { useClinicContext } from './useClinicContext';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import type {
  OrderSheetHistoryListResponse,
  OrderSheetHistorySearchParams,
} from '@/pages/main/sheetHistories/types';

const getHistories = async (
  siteType: string,
  clinicNumber: string,
  params: OrderSheetHistorySearchParams
): Promise<OrderSheetHistoryListResponse> => {
  const response = await apiClient.get<OrderSheetHistoryListResponse>(
    clinicApiPath(siteType, clinicNumber, '/sheets/histories'),
    { params: { search_order_date_from: params.search_order_date_from, search_order_date_to: params.search_order_date_to } }
  );
  return response.data;
};

/**
 * Hook to fetch order sheet histories (用紙発注履歴) for the given date range.
 * Enabled only when both tenant and clinicNumber and search params are set.
 */
export const useOrderSheetHistoriesQuery = (params: OrderSheetHistorySearchParams | null) => {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { data, isLoading, isError, isPending, refetch, error } = useQuery({
    queryKey: ['sheets', 'histories', tenant, clinicNumber, params],
    queryFn: () => getHistories(tenant!, clinicNumber!, params!),
    enabled: !!tenant && !!clinicNumber && !!params?.search_order_date_from && !!params?.search_order_date_to,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isError, isPending, refetch, error };
};
